import { createModel } from '@rematch/core';
import { generatePath } from 'react-router-dom';
import { toast } from 'react-toastify';

// Constants
import URL from '../../constants/api-constants';

// Types
import type { RootModel } from './index';

// Http
import http from '../http/http-common';

import { AxiosError } from 'axios';
import i18n from '../../infrastructure/i18n';

export type LocationContract = {
  id: string;
  end_at: Date;
  endless: boolean;
  start_at: Date;
};

export type CurrentLocation = {
  id: string;
  name: string;
  city: string;
  isCelsius: boolean;
};

export type Location = CurrentLocation & {
  contracts: LocationContract[];
};

export type LocationWithCycleCount = Location & {
  count: {
    active: number;
    completed: number;
    inactive: number;
    total: number;
  };
};

const DEFAULT_STATE: {
  locations: Location[] | null;
  locationsWithCycleCount: LocationWithCycleCount[] | null;
  location: Location | null;
  currentLocation: CurrentLocation | null;
} = {
  locations: null,
  locationsWithCycleCount: null,
  location: null,
  currentLocation: null,
};

export const locations = createModel<RootModel>()({
  state: DEFAULT_STATE,
  reducers: {
    set(state, payload) {
      return {
        ...state,
        ...payload,
      };
    },
    setOne(state, payload) {
      return {
        ...state,
        location: payload,
      };
    },
    setCurrentLocation(state, payload: CurrentLocation | null) {
      return { ...state, currentLocation: payload };
    },
    setList(state, payload) {
      return {
        ...state,
        locations: payload,
      };
    },
    setListWithCycleCount(state, payload) {
      return {
        ...state,
        locationsWithCycleCount: payload,
      };
    },
    resetOne(state) {
      return {
        ...state,
        location: DEFAULT_STATE.location,
      };
    },
    resetList(state) {
      return {
        ...state,
        locations: DEFAULT_STATE.locations,
      };
    },
    resetState() {
      return DEFAULT_STATE;
    },
  },
  effects: (dispatch) => {
    return {
      async getLocations(): Promise<Location[]> {
        try {
          const response = await http(URL.LOCATIONS);

          dispatch.locations.setList(response.data);

          return response.data;
        } catch (error: unknown) {
          if (error instanceof AxiosError) {
            toast.error(error.response?.data?.message || i18n.t('errors.unexpectedError'));
          } else if (error instanceof Error) {
            toast.error(error.message);
          } else {
            toast.error(i18n.t('errors.unexpectedError'));
          }
          throw error;
        }
      },

      async createLocation(payload) {
        try {
          await http.post(URL.LOCATIONS, payload);
          toast.success(i18n.t('clients.form.messages.create.success'));
        } catch (error: unknown) {
          if (error instanceof AxiosError) {
            toast.error(error.response?.data?.message || i18n.t('errors.unexpectedError'));
          } else if (error instanceof Error) {
            toast.error(error.message);
          } else {
            toast.error(i18n.t('errors.unexpectedError'));
          }
          throw error;
        }
      },

      async deleteLocation(id: string, state) {
        try {
          await http.delete(generatePath(URL.LOCATION, { id }));

          const { locations, locationsWithCycleCount } = state.locations || {};

          const updatedLocations = Array.isArray(locations)
            ? locations.filter((location: Location) => location.id !== id)
            : null;

          const updatedLocationsWithCycleCount = Array.isArray(locationsWithCycleCount)
            ? locationsWithCycleCount.filter((location: Location) => location.id !== id)
            : null;

          dispatch.locations.set({
            locations: updatedLocations,
            locationsWithCycleCount: updatedLocationsWithCycleCount,
          });

          toast.success(i18n.t('clients.form.messages.delete.success'));
        } catch (error: unknown) {
          if (error instanceof AxiosError) {
            toast.error(error.response?.data?.message || i18n.t('errors.unexpectedError'));
          } else if (error instanceof Error) {
            toast.error(error.message);
          } else {
            toast.error(i18n.t('errors.unexpectedError'));
          }
          throw error;
        }
      },

      async getLocation(id: string) {
        try {
          const path = generatePath(URL.LOCATION, { id });
          const response = await http.get(path);
          dispatch.locations.setOne(response.data);

          return {
            location: response.data,
          };
        } catch (error: unknown) {
          if (error instanceof AxiosError) {
            toast.error(error.response?.data?.message || i18n.t('errors.unexpectedError'));
          } else if (error instanceof Error) {
            toast.error(error.message);
          } else {
            toast.error(i18n.t('errors.unexpectedError'));
          }
          throw error;
        }
      },

      async getLocationsWithCycleCount() {
        try {
          const response = await http.get(URL.LOCATIONS_WITH_CYCLE_COUNT);
          dispatch.locations.setListWithCycleCount(response.data);

          return response.data;
        } catch (error: unknown) {
          if (error instanceof AxiosError) {
            toast.error(error.response?.data?.message || i18n.t('errors.unexpectedError'));
          } else if (error instanceof Error) {
            toast.error(error.message);
          } else {
            toast.error(i18n.t('errors.unexpectedError'));
          }
          throw error;
        }
      },

      //separated logic to avoid locations collisions
      async getCurrentLocation(id: string): Promise<CurrentLocation> {
        try {
          const path = generatePath(URL.LOCATION, { id });

          const response = await http.get<CurrentLocation>(path);

          dispatch.locations.setCurrentLocation(response.data);

          return response.data;
        } catch (error: unknown) {
          if (error instanceof AxiosError) {
            toast.error(error.response?.data?.message || i18n.t('errors.unexpectedError'));
          } else if (error instanceof Error) {
            toast.error(error.message);
          } else {
            toast.error(i18n.t('errors.unexpectedError'));
          }
          throw error;
        }
      },

      resetCurrentLocation() {
        dispatch.locations.setCurrentLocation(null);
      },

      async updateLocation({ id, formData }) {
        try {
          const response = await http.put(generatePath(URL.LOCATION, { id }), formData);
          dispatch.locations.setOne(response.data);

          toast.success(i18n.t('clients.form.messages.update.success'));
        } catch (error: unknown) {
          if (error instanceof AxiosError) {
            toast.error(error.response?.data?.message || i18n.t('errors.unexpectedError'));
          } else if (error instanceof Error) {
            toast.error(error.message);
          } else {
            toast.error(i18n.t('errors.unexpectedError'));
          }
          throw error;
        }
      },
    };
  },
});
